<script lang="ts">
  import { cx } from '@emotion/css';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { createEventDispatcher } from 'svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  let hasFocusOrHover = $state(false);

  // Svelte default values are initial values, not fallback values.

  interface Props {
    label?: string;
    disabled?: boolean;
    // To Avoid value being undefined after state updates, we need to set it to false.
    value?: boolean;
  }

  let { label = '', disabled = false, value = false }: Props = $props();
  let toggleValue = $derived(value ?? false);

  const dispatch = createEventDispatcher();

  const handleToggle = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    (event.target as HTMLElement).blur();

    toggleValue = !toggleValue;

    dispatch('change', toggleValue);
  };
  const responsiveVars = new ResponsiveVars()
    .addFluid('gap', toFluidVarInput(6, 6))
    .addFluid('padding-y', toFluidVarInput(6, 6))
    .addFluid('padding-x', toFluidVarInput(9, 9))
    .addFluid('font-size', toFluidVarInput(14, 14))
    .addFluid('track-width', toFluidVarInput(24, 24))
    .addFluid('track-height', toFluidVarInput(15, 15))
    .addFluid('handle-false-left', toFluidVarInput(3, 3))
    .addFluid('handle-false-offset-left', toFluidVarInput(5, 5))
    .addFluid('handle-true-left', toFluidVarInput(12, 12))
    .addFluid('handle-true-offset-left', toFluidVarInput(10, 10))
    .addFluid('handle-size', toFluidVarInput(9, 9))
    .addFluid('handle-top', toFluidVarInput(3, 3))
    .addFluid('handle-left', toFluidVarInput(3, 3))
    .toCSS();

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));
</script>

<!-- Follows naming (track, handle) in https://m3.material.io/components/switch/specs -->
<div
  class={cx('toggle', responsiveVars)}
  class:focus-hover={hasFocusOrHover}
  class:disabled
  role="switch"
  aria-checked={toggleValue}
  tabindex="0"
  onclick={(e) => {
    const isTouchDevice = matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (toggleValue && isTouchDevice) {
      hasFocusOrHover = false;
    }

    handleToggle(e);
  }}
  onkeypress={handleToggle}
  onmouseenter={() => (hasFocusOrHover = true)}
  onfocus={() => (hasFocusOrHover = true)}
  onmouseleave={() => (hasFocusOrHover = false)}
  onblur={() => (hasFocusOrHover = false)}
>
  <span class="label">
    {#if brandReskinEnabled}
      <FontSizeDecorator size="caption">
        {label}
      </FontSizeDecorator>
    {:else}
      {label}
    {/if}
  </span>
  <span class="track">
    <span class="handle"></span>
  </span>
</div>

<style lang="scss">
  @import 'src/scss/media';

  .toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--gap);
    border: 1px solid var(--twist-color-gray-100);
    border-radius: 1000px;
    padding: var(--padding-y) var(--padding-x);
    background-color: var(--twist-color-gray-0);
    color: var(--twist-color-gray-100);
    cursor: pointer;

    &:disabled {
      opacity: 40%;
    }

    &[aria-checked='true'] {
      .track {
        background-color: var(--twist-color-gray-100);
      }

      .handle {
        left: var(--handle-true-left);
      }

      &.focus-hover {
        @media (hover: hover) and (pointer: fine) {
          .track {
            background-color: var(--twist-color-gray-30);
          }

          .handle {
            left: var(--handle-true-offset-left);
          }
        }
      }
    }

    &[aria-checked='false'] {
      .track {
        background-color: var(--twist-color-gray-15);
      }
      .handle {
        left: var(--handle-false-left);
      }

      &.focus-hover {
        @media (hover: hover) and (pointer: fine) {
          .track {
            background-color: var(--twist-color-gray-100);
          }

          .handle {
            left: var(--handle-false-offset-left);
          }
        }
      }
    }

    &:active {
      opacity: 70%;

      &[aria-checked='true'] .track {
        background-color: var(--twist-color-gray-60);
      }
      &[aria-checked='false'] .track {
        background: var(--twist-color-gray-40);
      }
    }

    &:focus-visible {
      outline: none;
      border: 1px solid var(--twist-color-gray-100);
    }
  }

  .label {
    background: none;
    border: none;
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--font-size);
    font-style: normal;
    font-weight: 600;
    line-height: 100%;
    letter-spacing: 2%;
    transition: color 0.1s ease-in;

    &.selected {
      color: var(--twist-color-gray-0);
      pointer-events: none;
    }
  }

  .track {
    position: relative;
    display: inline-block;
    background-color: var(--twist-color-gray-15);
    border-radius: 1000px;
    height: var(--track-height);
    width: var(--track-width);
  }

  .handle {
    position: absolute;
    display: inline-block;
    border-radius: 1000px;
    background: var(--twist-color-gray-0);
    transition: left 0.4s;

    top: var(--handle-top);
    left: var(--handle-left);
    height: var(--handle-size);
    width: var(--handle-size);
  }
</style>
